import { useEffect, useRef } from "react";
import { Row } from "@tanstack/react-table";
import Icon from "@/components/Icon";
import { isCompactLayout } from "@/assets/theme/sizes";

interface ExpandableCountMultiCellProps {
  row: Row<any>;
  count: number;
  total?: number;
  hasItems: boolean;
  panelId: string;
  expanded: Record<string, string | null>;
  onExpand: (rowId: string, panelId: string | null) => void;
  showExpandIcon: boolean;
  className?: string;
  autoExpandRowId?: string | null;
  onAutoExpandComplete?: () => void;
}

/**
 * Module-level component for multi-panel expandable count cells.
 *
 * Unlike `ExpandableCountCell` (single toggle), this supports N expandable
 * columns per row. Each column has a `panelId`; clicking one column while
 * another is active swaps the panel without collapsing the row.
 *
 * Defined in its own file so React maintains a stable component identity,
 * which is required for the auto-expand `useEffect` to fire reliably.
 * `autoExpandRowId` expands once per mounted instance. Parents that rebuild
 * column defs each render must clear it via `onAutoExpandComplete` so collapse
 * is not undone by a remount.
 */
export default function ExpandableCountMultiCell({
  row,
  count,
  total,
  hasItems,
  panelId,
  expanded,
  onExpand,
  showExpandIcon,
  className,
  autoExpandRowId,
  onAutoExpandComplete
}: ExpandableCountMultiCellProps) {
  const compact = isCompactLayout();
  const isActivePanel = expanded[row.id] === panelId;
  const shouldShowIcon = showExpandIcon && hasItems && !compact;
  const displayText = total !== undefined ? `${count} of ${total}` : `${count}`;
  const autoExpandedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!autoExpandRowId) {
      autoExpandedIdRef.current = null;
      return;
    }

    if (autoExpandedIdRef.current === autoExpandRowId) {
      return;
    }

    if (autoExpandRowId === row.id && hasItems && !row.getIsExpanded()) {
      autoExpandedIdRef.current = autoExpandRowId;
      row.getToggleExpandedHandler()();
      onExpand(row.id, panelId);
      onAutoExpandComplete?.();
    }
  }, [autoExpandRowId, row, hasItems, panelId, onExpand, onAutoExpandComplete]);

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    const isRowExpanded = row.getIsExpanded();
    const willCollapse = isActivePanel && isRowExpanded;

    if (willCollapse || !isRowExpanded) {
      row.getToggleExpandedHandler()();
    }

    onExpand(row.id, isActivePanel ? null : panelId);
  }

  return (
    <div
      className={`flex items-center gap-2 ${compact ? "w-auto" : "min-h-12 w-full -mx-4 -my-2 px-4 py-2"} ${hasItems ? "cursor-pointer" : ""} ${className || ""}`}
      onClick={hasItems ? handleClick : undefined}
    >
      <span>{displayText}</span>
      {shouldShowIcon && <Icon name={isActivePanel ? "expand_less" : "expand_more"} />}
    </div>
  );
}
